import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Paper, Divider, Chip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import config from '../../../../config';

/**
 * Comprehensive Plan Change Modal
 * Shows detailed comparison between current and new plan including:
 * - Plan details (name, price, credits)
 * - Feature comparison
 * - Token/credit differences
 * - Proration details
 * - Billing information
 *
 * User confirms in this modal, then payment is triggered immediately
 */
const PlanChangeModal = ({ open, onClose, onConfirm, currentPlan, newPlan, subscriptionId, storeId, nextDueDate }) => {
  const [loading, setLoading] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch proration data when modal opens
  useEffect(() => {
    if (open && storeId && currentPlan && newPlan) {
      fetchProrationData();
    }
  }, [open, storeId, currentPlan, newPlan]);

  // Fetches proration calculation from backend
  const fetchProrationData = async () => {
    console.log("new plan", newPlan)
    try {
      setLoading(true);
      setError(null);
      const url = `${config.API_BASE_V2_URL}/subscriptions/proration`;
      const payload = {
        "store_id": storeId,
        "subscription_id": subscriptionId,
        "new_plan_id": newPlan.plan_id
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await response.json();
      if (json.success) {
        setProrationData(json);  // Now matches new structure
      } else {
        setError(json.error || 'Failed to calculate proration');
      }
    } catch (err) {
      console.error('Proration fetch error:', err);
      setError('Failed to fetch proration details');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!prorationData || error) return;
    onConfirm(newPlan, prorationData);
    onClose();
  };

  /**
   * Parse features from plan_features field
   * Handles multiple formats: array, string (JSON), object
   */
  const parseFeatures = (features) => {
    if (!features) return [];
    if (Array.isArray(features)) return features;
    if (typeof features === "string") {
      try {
        const parsed = JSON.parse(features);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    if (typeof features === "object") {
      return Object.keys(features).filter((k) => features[k]);
    }
    return [];
  };

  const currentFeatures = parseFeatures(currentPlan?.planfeatures);
  const newFeatures = parseFeatures(newPlan?.planfeatures);
  const allFeatures = [...new Set([...currentFeatures, ...newFeatures])];
  const tokenDiff = (newPlan?.tokens || 0) - (currentPlan?.tokens || 0);

  const direction = prorationData?.direction || null;
  const isUpgrade = direction === 'upgrade';
  const isDowngrade = direction === 'downgrade';
  const prorationAmountCents = prorationData?.proration_amount_cents || 0;
  const cashDelta = prorationData?.cash?.cash_delta || 0;
  const remainingDays = prorationData?.cash?.remaining_days || 0;
  const totalDays = prorationData?.meta?.old_validity_days || 30;
  const oldPlanId = prorationData?.meta?.old_plan_id;
  const newPlanId = prorationData?.meta?.new_plan_id;
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* Modal Header */}
      <DialogTitle
        sx={{
          bgcolor: isUpgrade
            ? "rgba(33, 150, 243, 0.08)"
            : "rgba(255, 152, 0, 0.08)",
          borderBottom: isUpgrade
            ? "2px solid rgba(33, 150, 243, 0.3)"
            : "2px solid rgba(255, 152, 0, 0.3)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          {isUpgrade ? (
            <TrendingUpIcon sx={{ color: "primary.main", fontSize: 28 }} />
          ) : (
            <TrendingDownIcon sx={{ color: "warning.main", fontSize: 28 }} />
          )}
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {isUpgrade ? "Upgrade" : "Downgrade"} Subscription Plan
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Review the changes before confirming
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      {/* Modal Content - Compact, No Scroll */}
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Calculating proration and loading plan details...
            </Typography>
          </Box>
        ) : error ? (
          <Box py={2}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : prorationData ? (
          <Box>
            {/* Plan Comparison Section - Compact */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                mb={1}
                display="block"
              >
                PLAN COMPARISON
              </Typography>

              <Box display="flex" gap={2}>
                {/* Current Plan - Compact */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    bgcolor: "rgba(0, 0, 0, 0.02)",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontSize="10px"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    CURRENT PLAN
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} mt={0.3} mb={1}>
                    {currentPlan?.plan_name}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" mb={0.4}>
                    <Typography variant="caption" color="text.secondary">
                      Price:
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>
                      ${currentPlan?.plan_amount}/mo
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={0.4}>
                    <Typography variant="caption" color="text.secondary">
                      Credits:
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>
                      {currentPlan?.tokens?.toLocaleString()}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Billing:
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>
                      {currentPlan?.billing_cycle}
                    </Typography>
                  </Box>
                </Paper>

                {/* New Plan - Compact */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    bgcolor: isUpgrade
                      ? "rgba(33, 150, 243, 0.08)"
                      : "rgba(255, 152, 0, 0.08)",
                    border: isUpgrade
                      ? "2px solid rgba(33, 150, 243, 0.3)"
                      : "2px solid rgba(255, 152, 0, 0.3)",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontSize="10px"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    NEW PLAN
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} mt={0.3} mb={1}>
                    {newPlan?.plan_name}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" mb={0.4}>
                    <Typography variant="caption" color="text.secondary">
                      Price:
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      ${newPlan?.plan_amount}/mo
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mb={0.4}>
                    <Typography variant="caption" color="text.secondary">
                      Credits:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" fontWeight={600}>
                        {newPlan?.tokens?.toLocaleString()}
                      </Typography>
                      {tokenDiff !== 0 && (
                        <Chip
                          label={`${tokenDiff > 0 ? "+" : ""}${tokenDiff.toLocaleString()}`}
                          size="small"
                          color={tokenDiff > 0 ? "success" : "warning"}
                          sx={{ height: 16, fontSize: 9, fontWeight: 600, px: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Billing:
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {newPlan?.billing_cycle}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* UPGRADE: Unlocked Features + Token Balance Side by Side */}
            {isUpgrade && (newPlan?.plan_name === "Pro" || newPlan?.plan_name === "Premium") && (
              <Box sx={{ mb: 2.5 }}>
                <Box display="flex" gap={2}>
                  {/* Left: Unlocked Features */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: "rgba(76, 175, 80, 0.06)",
                      border: "1px solid rgba(76, 175, 80, 0.25)",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="success.dark"
                      display="block"
                      mb={1.5}
                    >
                      🎨 UNLOCKED FEATURES
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "success.main",
                          }}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          Custom Welcome Message
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "success.main",
                          }}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          Theme Color Customization
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "success.main",
                          }}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          Custom Font Styles
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "success.main",
                          }}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          Custom Chatbot Logo
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Right: Token Balance */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: "rgba(33, 150, 243, 0.06)",
                      border: "1px solid rgba(33, 150, 243, 0.25)",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="primary.dark"
                      display="block"
                      mb={1.5}
                    >
                      💎 TOKEN BALANCE
                    </Typography>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Current tokens:
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {(8500).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Monthly allocation:
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="success.main"
                      >
                        +{(newPlan?.tokens || 0).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                      }}
                    >
                      <Typography variant="caption" fontWeight={600}>
                        Total after upgrade:
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="primary.main">
                        {(prorationData?.credits?.credits_remaining || 0 + (newPlan?.tokens || 0)).toLocaleString()}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}

            {/* DOWNGRADE: Missing Features + Token Balance Side by Side */}
            {isDowngrade && (currentPlan?.plan_name === "Pro" || currentPlan?.plan_name === "Premium") && newPlan?.plan_name === "Basic" && (
              <Box sx={{ mb: 2.5 }}>
                <Box display="flex" gap={2}>
                  {/* Left: Missing Features */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: "rgba(255, 152, 0, 0.06)",
                      border: "1px solid rgba(255, 152, 0, 0.25)",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="warning.dark"
                      display="block"
                      mb={1.5}
                    >
                      ⚠️ FEATURES YOU'LL LOSE
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "warning.main",
                          }}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          Custom Welcome Message
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "warning.main",
                          }}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          Theme Color Customization
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "warning.main",
                          }}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          Custom Font Styles
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            bgcolor: "warning.main",
                          }}
                        />
                        <Typography variant="caption" fontWeight={500}>
                          Custom Chatbot Logo
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Right: Token Balance */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      p: 2,
                      bgcolor: "rgba(255, 152, 0, 0.06)",
                      border: "1px solid rgba(255, 152, 0, 0.25)",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="warning.dark"
                      display="block"
                      mb={1.5}
                    >
                      💎 TOKEN BALANCE
                    </Typography>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Current tokens:
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {prorationData?.credits?.credits_remaining?.toLocaleString() || '0'}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Monthly allocation:
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="warning.main"
                      >
                        {(newPlan?.tokens || 0).toLocaleString()}
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                      }}
                    >
                      <Typography variant="caption" fontWeight={600}>
                        Total after downgrade:
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="warning.main">
                        {(prorationData?.credits?.credits_remaining || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}

            {/* Feature Comparison - Compact */}
            {allFeatures.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  mb={1}
                  display="block"
                >
                  FEATURE COMPARISON
                </Typography>

                {/* Header Row */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 0.5, px: 0.5 }}
                >
                  <Typography variant="caption" fontSize="11px" color="text.secondary">
                    Feature
                  </Typography>
                  <Box display="flex" gap={2.5}>
                    <Typography
                      variant="caption"
                      fontSize="11px"
                      color="text.secondary"
                      sx={{ minWidth: 60, textAlign: "center" }}
                    >
                      Current
                    </Typography>
                    <Typography
                      variant="caption"
                      fontSize="11px"
                      color="text.secondary"
                      sx={{ minWidth: 60, textAlign: "center" }}
                    >
                      New
                    </Typography>
                  </Box>
                </Box>

                {/* Feature Rows - Compact */}
                {allFeatures.map((feature, idx) => {
                  const inCurrent = currentFeatures.includes(feature);
                  const inNew = newFeatures.includes(feature);
                  const isNewFeature = inNew && !inCurrent;

                  return (
                    <Box
                      key={idx}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        py: 0.5,
                        px: 0.5,
                        mb: 0.3,
                        borderRadius: 0.5,
                        bgcolor: isNewFeature
                          ? "rgba(76, 175, 80, 0.06)"
                          : "transparent",
                        border: isNewFeature
                          ? "1px solid rgba(76, 175, 80, 0.15)"
                          : "1px solid transparent",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontSize="12px"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {feature.replace(/_/g, " ")}
                      </Typography>
                      <Box display="flex" gap={2.5}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            minWidth: 60,
                          }}
                        >
                          {inCurrent ? (
                            <CheckCircleIcon
                              sx={{ fontSize: 16, color: "success.main" }}
                            />
                          ) : (
                            <CancelIcon
                              sx={{ fontSize: 16, color: "text.disabled" }}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 60,
                          }}
                        >
                          {inNew ? (
                            <CheckCircleIcon
                              sx={{ fontSize: 16, color: "success.main" }}
                            />
                          ) : (
                            <CancelIcon
                              sx={{ fontSize: 16, color: "text.disabled" }}
                            />
                          )}
                          {isNewFeature && (
                            <Chip
                              label="NEW"
                              size="small"
                              color="success"
                              sx={{ height: 14, fontSize: 8, fontWeight: 600, px: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Billing Details - Compact */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                mb={1}
                display="block"
              >
                BILLING DETAILS
              </Typography>

              {remainingDays && totalDays && (
                <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                  <InfoOutlinedIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                  <Typography variant="caption" fontSize="11px" color="text.secondary">
                    {remainingDays} of {totalDays} days remaining
                  </Typography>
                </Box>
              )}

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  p: 1.5,
                  bgcolor: isUpgrade
                    ? "rgba(33, 150, 243, 0.05)"
                    : "rgba(255, 152, 0, 0.05)",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {isDowngrade ? "Refund Amount:" : "Due Today:"}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={isUpgrade ? "primary.main" : "warning.main"}
                >
                  ${Math.abs(prorationAmountCents / 100).toFixed(2)}
                </Typography>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
              >
                <Typography variant="caption" color="text.secondary">
                  Next billing amount:
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  ${newPlan?.plan_amount?.toFixed(2) || "0.00"}/month
                </Typography>
              </Box>

              {nextDueDate && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Next billing date:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {new Date(nextDueDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Info Box - Compact */}
            <Paper
              elevation={0}
              sx={{
                mt: 1.5,
                p: 1.5,
                bgcolor: "rgba(33, 150, 243, 0.04)",
                border: "1px solid rgba(33, 150, 243, 0.12)",
                borderRadius: 1,
              }}
            >
              <Box display="flex" gap={0.8}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 16, color: "primary.main", mt: 0.1 }}
                />
                <Typography variant="caption" fontSize="11px" color="text.secondary" lineHeight={1.5}>
                  {isUpgrade 
                    ? "Plan upgraded immediately upon payment. Prorated amount charged for remaining days." 
                    : "Plan downgraded at period end. Refund issued for unused portion."
                  }
                </Typography>
              </Box>
            </Paper>
          </Box>
        ) : null}
      </DialogContent>

      {/* Modal Actions */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "rgba(0, 0, 0, 0.02)" }}>
        <Button onClick={onClose} variant="outlined" size="large">
          Cancel
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={loading || !prorationData || !!error}
          onClick={handleConfirm}
          sx={{ minWidth: 120 }}
        >
          {loading ? "Loading..." : "Make Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanChangeModal;
